/**
 * PlayerEligibilityTab — Position eligibility breakdown for player profile modal.
 *
 * Section 1: Current eligible positions (EligibilityBadges)
 * Section 2: Games by position table (per-season breakdown)
 * Section 3: Commissioner override indicator
 */
import React from 'react';
import { dynastyTheme } from '../../services/colorService';
import EligibilityBadges from '../EligibilityBadges';

const PlayerEligibilityTab = ({ player, statsSeasonYear }) => {
  if (!player) return null;

  const roster = player.league_context?.roster || {};
  const effective = roster.effective_eligibility || [];
  const custom = roster.custom_eligibility;
  const bySeasonRaw = roster.eligibility_by_season || {};
  // Eligibility threshold comes from league settings (no fallback — if the
  // backend didn't populate it, we don't invent a number).
  const threshold = roster.eligibility_threshold;
  const primaryPosition = player.info?.position || '';

  const currentSeason = statsSeasonYear;
  const priorSeason = currentSeason ? currentSeason - 1 : null;

  // Build table rows from per-season data
  const positions = Object.keys(bySeasonRaw).sort((a, b) => {
    // Primary position first, then by best-season games descending
    if (a === primaryPosition) return -1;
    if (b === primaryPosition) return 1;
    const aMax = Math.max(bySeasonRaw[a]?.current || 0, bySeasonRaw[a]?.prior || 0);
    const bMax = Math.max(bySeasonRaw[b]?.current || 0, bySeasonRaw[b]?.prior || 0);
    return bMax - aMax;
  });

  const sectionTitle = (text) => (
    <div className={`text-xs font-bold ${dynastyTheme.classes.text.primary} uppercase tracking-wide mb-2`}>
      {text}
    </div>
  );

  return (
    <div className="p-4 space-y-5">
      {/* Section 1: Current Eligibility */}
      {sectionTitle('Current Eligibility')}
      <div className="mb-3">
        <EligibilityBadges player={player} />
      </div>

      {/* Section 2: Games by Position */}
      {positions.length > 0 && currentSeason && (
        <>
          {sectionTitle('Games by Position')}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={`border-b ${dynastyTheme.classes.border.neutral}`}>
                  <th className={`py-2 px-3 text-left ${dynastyTheme.classes.text.white} font-semibold`}>Position</th>
                  {priorSeason && <th className={`py-2 px-3 text-center ${dynastyTheme.classes.text.neutralLight}`}>{priorSeason}</th>}
                  <th className={`py-2 px-3 text-center ${dynastyTheme.classes.text.neutralLight}`}>{currentSeason}</th>
                  <th className={`py-2 px-3 text-center ${dynastyTheme.classes.text.neutralLight}`} title="Best of the two seasons — eligibility is earned in either season, not combined">Best</th>
                  <th className={`py-2 px-3 text-center ${dynastyTheme.classes.text.neutralLight}`}>Status</th>
                </tr>
              </thead>
              <tbody>
                {positions.map(pos => {
                  const data = bySeasonRaw[pos] || {};
                  const prior = data.prior || 0;
                  const current = data.current || 0;
                  // Eligibility: earned in EITHER season independently (not summed).
                  const best = Math.max(prior, current);
                  const isEligible = threshold != null && best >= threshold;
                  // "More needed" counts only what can still be earned: the
                  // prior season is closed, so remaining = threshold - current.
                  const remaining = threshold != null ? Math.max(0, threshold - current) : null;

                  return (
                    <tr key={pos} className={`border-b ${dynastyTheme.classes.border.neutral}`}>
                      <td className={`py-2 px-3 font-semibold ${dynastyTheme.classes.text.white}`}>{pos}</td>
                      {priorSeason && (
                        <td className={`py-2 px-3 text-center ${dynastyTheme.classes.text.neutralLight}`}>
                          {prior || '-'}
                        </td>
                      )}
                      <td className={`py-2 px-3 text-center ${dynastyTheme.classes.text.white}`}>
                        {current || '-'}
                      </td>
                      <td className={`py-2 px-3 text-center font-semibold ${dynastyTheme.classes.text.white}`}>
                        {best}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {isEligible ? (
                          <span className="text-green-400 font-semibold">Eligible</span>
                        ) : remaining != null ? (
                          <span className={dynastyTheme.classes.text.neutralLight}>
                            {remaining} more needed
                          </span>
                        ) : (
                          <span className={dynastyTheme.classes.text.neutralLighter}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {threshold != null && (
            <div className={`text-[10px] ${dynastyTheme.classes.text.neutralLighter} mt-1`}>
              Eligibility threshold: {threshold} games at a position in EITHER the current or prior season (not combined). "More needed" counts only current-season games — the prior season is closed.
            </div>
          )}
        </>
      )}

      {/* Section 3: Commissioner Override */}
      {custom && custom.length > 0 && (
        <>
          {sectionTitle('Commissioner Override')}
          <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20`}>
            <span className={`${dynastyTheme.classes.text.primary} font-semibold`}>Commissioner override active</span>
            <span className="ml-2">
              — Eligible at: {custom.join(', ')}
            </span>
          </div>
        </>
      )}

      {/* Empty state */}
      {positions.length === 0 && !custom && (
        <div className={`text-xs ${dynastyTheme.classes.text.neutralLighter} text-center py-6`}>
          No position eligibility data available
        </div>
      )}
    </div>
  );
};

export default PlayerEligibilityTab;
